import Colors from '../../utils/color';
import getStatusBarColor from '../../utils/getStatusBarColor';
import { useAppSelector } from '../../app/hooks';
import {
  selectAIStatus,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectRequiresTakeover,
  selectIsAIEscalatedOnCarEnter,
  selectIsAIAutoMode,
} from '../../redux/features/ai/ai.selector';
import {
  AIStatusColors,
  DEFAULT_STATUS_MESSAGE,
} from '../../redux/features/ai/ai.constants';
import {
  selectFeatureFlagUnresolvedItems,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectAIIndicator } from '../../selectors/restaurant';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';

/**
 * HITL 7.0 band palette (HITL-1661).
 *
 * 3.0 gave the band its own greens, yellows and reds, chosen in isolation. Put
 * full width under the header, those sat next to the header indicators and the
 * intent buttons and read as three different design languages on one screen.
 * 7.0 drops the private palette and reuses the colours already in front of the
 * agent, so the same state means the same colour wherever they look:
 *
 *   state    background                        matches                text
 *   ───────  ────────────────────────────────  ─────────────────────  ──────────
 *   green    brightGreen  #70e000              header mic/wifi/car    near-black
 *   yellow   acknowledge  #FFC107              Conversation intents   near-black
 *   red      requestStaff #FF0048              the Staff button       white
 *
 * Staff intervention already used torchRed, which is the same value as
 * requestStaff, so it matched the Staff button before this change and is
 * untouched by it.
 *
 * The `orange` member of AIStatusColors has no mapping here for the same reason
 * getStatusBarColor's switch has no case for it — it falls through to green.
 * Car-enter escalation keeps its own orange identity separately, below.
 */
const HITL7_STATUS_PALETTE: Partial<
  Record<AIStatusColors, { background: string; textColor: string }>
> = {
  [AIStatusColors.green]: {
    background: Colors.brightGreen,
    textColor: Colors.primaryButtonTextColor,
  },
  [AIStatusColors.yellow]: {
    background: Colors.acknowledge,
    textColor: Colors.primaryButtonTextColor,
  },
  [AIStatusColors.red]: {
    background: Colors.requestStaff,
    textColor: Colors.white,
  },
};

const HITL7_FALLBACK = {
  background: Colors.brightGreen,
  textColor: Colors.primaryButtonTextColor,
};

export interface AIStatusBandState {
  /** False for pure-HITL restaurants — they never show an AI status band. */
  show: boolean;
  message: string;
  /** Ignored while `blink` is true; the animation owns the background then. */
  background: string;
  textColor: string;
  blink: boolean;
}

/**
 * The AI status band's state, headless.
 *
 * Extracted from CustomerPulse (HITL-1660) so the band can render in two
 * places without the precedence ladder being written twice: folded into the
 * Copilot card under hitl_3_0, and full width under the header under
 * hitl_7_0. The logic below is unchanged — only its home moved.
 *
 * ── Precedence (highest first), all gated on an ACTIVE SESSION ──────────────
 *
 *   condition                           band shows              colour
 *   ──────────────────────────────────  ──────────────────────  ──────────────
 *   staff intervention                  "Staff Intervention"    torch red
 *   agent intercepted (isAIAutoMode 0)  "Agent Intercept"       red (static)
 *   takeover requested, not yet taken   "take over now" (blink) red (pulsing)
 *   escalation (recoverable), pre-take  escalation reason       red / orange
 *   otherwise                           live AI status          green/yellow/red
 */
const useAIStatusBand = (): AIStatusBandState => {
  const aiStatus = useAppSelector(selectAIStatus);
  const isAIEscalation = useAppSelector(selectIsAIEscalation);
  const aiEscalationReason = useAppSelector(selectAIEscalationReason);
  const requiresTakeover = useAppSelector(selectRequiresTakeover);
  const isAIEscalatedOnCarEnter = useAppSelector(selectIsAIEscalatedOnCarEnter);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const unresolvedItemsEnabled = useAppSelector(
    selectFeatureFlagUnresolvedItems
  );
  const sessionId = useAppSelector(selectSessionId);
  const aiEnabled = useAppSelector(selectAIIndicator);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const { isStaffInterventionHappening } = useIntervention();

  // ── Why the band gates on the SESSION, not the car ──────────────────────────
  // The AI control state is deliberately sticky — resetting it on car_exit caused
  // a prod incident (dropped order + cart revert). A drive-thru takeover lets the
  // car leave (car_exit) while the SAME session's order is still being finished,
  // so the band must follow an active session, NOT car presence:
  //
  //   signal            cleared on car_exit?          role
  //   ────────────────  ────────────────────────────  ─────────────────────────
  //   isAIAutoMode      no — sticky to session end     agent-intercepted flag
  //   requiresTakeover  no — sticky to session end     takeover alert
  //   isAIEscalation    no — sticky to session end     escalation flag
  //   sessionId         no while an order is pending   "is the session live?"
  //   isCarPresent      YES, immediately               (removed — too blunt)
  //
  // car_exit with a pending order only arms the post-car-exit timer; sessionId
  // clears only when the session TRULY ends (Finish/Cancel, final check, empty-
  // cart car_exit, kill switch, timeout). Gating on sessionId holds the intercept/
  // escalation status until the session ends, then idles to green.
  const sessionActive = Boolean(sessionId);

  // ── Interception & takeover ─────────────────────────────────────────────────
  // ANY agent interception (isAIAutoMode → false) during an active session shows
  // a static "Agent Intercept" — not just hard takeovers. A takeover escalation
  // ADDITIONALLY blinks a "take over now" alert while the AI is still in control,
  // before the agent acts:
  //
  //   state                                   band
  //   ──────────────────────────────────────  ───────────────────────────────
  //   takeover requested, not yet taken over  BLINKS "take over now" (showPulse)
  //   agent intercepted (any escalation)      static "Agent Intercept"
  //
  // isAIAutoMode and requiresTakeover are both sticky, so these hold for the rest
  // of the session. showPulse stays gated on the takeover flag + unresolved-items
  // feature; the plain "Agent Intercept" label does not.
  const takeoverActive =
    unresolvedItemsEnabled && requiresTakeover && sessionActive;
  const showPulse = takeoverActive && isAIAutoMode;
  const agentIntercepted = !isAIAutoMode && sessionActive;
  const showEscalation = isAIEscalation && sessionActive;
  // Between sessions (no active session) the band idles on the default green —
  // the live aiStatus/escalation describe a conversation that is over.
  const displayedAIStatus = sessionActive
    ? aiStatus
    : { status: AIStatusColors.green, message: DEFAULT_STATUS_MESSAGE };

  // ── Pure-HITL parity ────────────────────────────────────────────────────────
  // The legacy AIStatusBar mounted only for AI restaurants (isAIActive), so a
  // pure-HITL restaurant never showed an AI status bar. Match that here:
  //
  //   restaurant    band?   staff-intervention cue
  //   ────────────  ──────  ──────────────────────────────
  //   AI enabled    yes     band + omnibar red border
  //   pure HITL     no      omnibar red border only
  //
  // ── Intercept clears the band under hitl_7_0 (HITL-1661) ────────────────────
  // Once an agent has taken the conversation, the band is narrating a state the
  // agent is now driving themselves. Under 3.0 it lingers as a static "Agent
  // Intercept" strip, competing for attention with nothing left to say; under
  // 7.0 it goes away, which is what the screen did before 3.0.
  //
  // Staff intervention is deliberately NOT swept up in this. The reasoning is
  // the mirror image of the intercept case: on intercept the agent is the one
  // driving, so the band is telling them something they already know, whereas
  // during a staff intervention the agent can do nothing at all about it. The
  // band is then their only notice that control has left their hands, so it
  // stays — even when they have also intercepted.
  //
  // selectAIIndicator is typed as the Boolean wrapper; coerce to a primitive
  // so callers get a plain boolean they can use in a `!show` guard.
  const clearedByIntercept =
    isHitl7 && agentIntercepted && !isStaffInterventionHappening;
  const show = Boolean(aiEnabled) && !clearedByIntercept;

  // Staff intervention outranks every AI status: a human now owns the order, so
  // the band reads "Staff Intervention" (static, no blink). Otherwise prefer the
  // escalation reason when one is set, falling back to the live status message —
  // which displayedAIStatus.message already resolves to (the green default when
  // idle), so it is the single reliable fallback for every non-flagged state.
  const message = isStaffInterventionHappening
    ? 'Staff Intervention'
    : agentIntercepted
    ? 'Agent Intercept'
    : (showEscalation && aiEscalationReason) || displayedAIStatus.message;

  // The takeover blink is suppressed during staff intervention — the static
  // "hand-off" red is the resolved, unambiguous state.
  const blink = showPulse && !isStaffInterventionHappening;

  // ── Band background / text colour (matches legacy AIStatusBar) ───────────────
  //   state                background          text
  //   ───────────────────  ──────────────────  ─────────────────────────────
  //   staff intervention   torch red           white
  //   agent intercepted    red (static)        white
  //   escalation / status  getStatusBarColor   white on red/orange, else inherit
  //
  // `blink` drives the vivid takeover animation instead of these colours;
  // agentIntercepted is a HITL 3.0-only static state (the legacy bar unmounts on
  // intercept) rendered in the same vivid escalation red.
  // Escalation is pulled out of getStatusBarColor here rather than passed into
  // it, so the 7.0 branch can swap the plain-escalation red without touching the
  // helper the legacy bar still shares. Flag off, every arm below resolves to
  // exactly what getStatusBarColor returned before.
  const hitl7 =
    HITL7_STATUS_PALETTE[displayedAIStatus.status] ?? HITL7_FALLBACK;

  let background: string;
  let textColor: string;
  if (isStaffInterventionHappening) {
    background = Colors.torchRed;
    textColor = Colors.white;
  } else if (agentIntercepted) {
    // 7.0 never reaches this arm — `show` is already false by now (unless staff
    // intervention took the branch above), so this is the 3.0 strip only.
    background = Colors.red;
    textColor = Colors.white;
  } else if (showEscalation) {
    // Car-enter escalation keeps its orange identity in both versions; it is a
    // distinct state, not a severity, and no button on screen shares it.
    background = isAIEscalatedOnCarEnter
      ? Colors.orange
      : (isHitl7 && Colors.requestStaff) || Colors.red;
    textColor = Colors.white;
  } else if (isHitl7) {
    background = hitl7.background;
    textColor = hitl7.textColor;
  } else {
    background = getStatusBarColor(false, false, displayedAIStatus.status);
    textColor = 'inherit';
  }

  return { show, message, background, textColor, blink };
};

export default useAIStatusBand;
